<template>
  <v-container
    class="border rounded-lg bg-grey-lighten-4 mt-2"
    :class="[
      {
        'disabled-settings':
          disableProductPlacement || isShufflingPlacement || canvasStore.canvasLoader,
      },
    ]"
  >
    <v-row v-show="filteredPlacementOptions.length !== 1">
      <v-col class="text-start pt-1 text-subtitle-1 text-medium-emphasis pt-2" cols="3">
        <div class="d-inline-flex align-center">
          <span>{{ t('labels.arrangement') }}</span>
          <v-btn
            width="30"
            height="30"
            size="x-small"
            rounded-sm
            variant="tonal"
            class="ml-1 opacity-1"
            :color="editorStore.isPlacementLocked ? 'primary' : 'black'"
            @click="editorStore.togglePlacementLocked()"
          >
            <v-icon size="20">
              {{ editorStore.isPlacementLocked ? 'mdi-lock' : 'mdi-lock-open-variant' }}
            </v-icon>
          </v-btn>
        </div>
      </v-col>
      <v-col class="pt-1">
        <v-chip-group
          v-model="placementStore.selectedPlacementOption"
          column
          mandatory
          class="d-flex justify-start"
        >
          <v-btn
            v-for="(option, index) in filteredPlacementOptions"
            :key="index"
            :value="option"
            class="text-capitalize mr-2 mb-2"
            :color="placementStore.selectedPlacementOption === option ? 'primary' : ''"
            variant="outlined"
            rounded="pill"
            @click="
              !disableProductPlacement && undoRedoService.handlePlacementChangeWithUndo(option)
            "
          >
            {{ option }}
          </v-btn>
        </v-chip-group>
      </v-col>
    </v-row>
    <v-row
      class="mt-0 align-center"
      :class="{ 'pb-2': isTiltDisabled, 'mt-n3': filteredPlacementOptions.length === 1 }"
    >
      <v-col class="text-start pt-1 text-subtitle-1 text-medium-emphasis" cols="3">
        {{ t('labels.productsSize') }}
      </v-col>
      <v-col class="text-start">
        <v-btn-group color="grey-lighten-2" density="comfortable" rounded="pill">
          <v-btn
            icon="mdi-minus"
            :disabled="!decreaseProductSize"
            :color="!decreaseProductSize ? 'disabled' : 'grey-lighten-2'"
            @click="scaleProducts(false)"
          ></v-btn>
          <v-btn icon="mdi-image-size-select-large" readonly></v-btn>
          <v-btn
            icon="mdi-plus"
            :disabled="!increaseProductSize"
            :color="!increaseProductSize ? 'disabled' : 'grey-lighten-2'"
            @click="scaleProducts(true)"
          ></v-btn>
        </v-btn-group>
      </v-col>
    </v-row>
    <v-row class="mt-4" v-if="!isTiltDisabled">
      <v-col class="text-start pt-2 text-subtitle-1 text-medium-emphasis" cols="3">
        {{ t('labels.productTilt') }}
      </v-col>
      <v-col class="text-start pt-1" cols="9">
        <!-- 1 product: 1 control -->
        <v-btn-group
          v-if="productCount === 1"
          color="grey-lighten-2"
          density="comfortable"
          rounded="pill"
          class="mr-4 mb-2 tilt-option"
          :class="
            selectedTiltStrategy?.value === TILT_STRATEGIES.SINGLE_PRODUCT.LEFT
              ? 'tilt-option--active'
              : ''
          "
        >
          <v-btn
            icon="mdi-rotate-left"
            :disabled="
              isTiltDisabled || selectedTiltStrategy?.value !== TILT_STRATEGIES.SINGLE_PRODUCT.LEFT
            "
            :color="
              isTiltDisabled || selectedTiltStrategy?.value !== TILT_STRATEGIES.SINGLE_PRODUCT.LEFT
                ? 'disabled'
                : 'grey-lighten-2'
            "
            @click="
              !isTiltDisabled && handleProductTilt(TILT_STRATEGY_OBJECTS.SINGLE_PRODUCT.LEFT, false)
            "
          ></v-btn>
          <v-btn
            class="icon-btn"
            :class="
              selectedTiltStrategy?.value !== TILT_STRATEGIES.SINGLE_PRODUCT.LEFT
                ? 'text-medium-emphasis'
                : ''
            "
            :disabled="isTiltDisabled"
            @click="
              !isTiltDisabled && changeTiltStrategy(TILT_STRATEGY_OBJECTS.SINGLE_PRODUCT.LEFT)
            "
          >
            <svg width="20" height="16" viewBox="0 0 20 16" fill="none">
              <rect x="8.5" y="0" width="3" height="16" fill="currentColor" />
            </svg>
          </v-btn>
          <v-btn
            icon="mdi-rotate-right"
            :disabled="
              isTiltDisabled || selectedTiltStrategy?.value !== TILT_STRATEGIES.SINGLE_PRODUCT.LEFT
            "
            :color="
              isTiltDisabled || selectedTiltStrategy?.value !== TILT_STRATEGIES.SINGLE_PRODUCT.LEFT
                ? 'disabled'
                : 'grey-lighten-2'
            "
            @click="
              !isTiltDisabled && handleProductTilt(TILT_STRATEGY_OBJECTS.SINGLE_PRODUCT.LEFT, true)
            "
          ></v-btn>
        </v-btn-group>

        <!-- 2 products: 4 controls -->
        <template v-else-if="productCount === 2">
          <!-- 1st button: Both products tilt same direction -->
          <v-btn-group
            color="grey-lighten-2"
            density="comfortable"
            rounded="pill"
            class="mr-4 mb-2 tilt-option"
            :class="
              selectedTiltStrategy?.value === TILT_STRATEGIES.DUAL_PRODUCT.BOTH_SAME
                ? 'tilt-option--active'
                : ''
            "
          >
            <v-btn
              icon="mdi-rotate-left"
              :disabled="
                isTiltDisabled ||
                selectedTiltStrategy?.value !== TILT_STRATEGIES.DUAL_PRODUCT.BOTH_SAME
              "
              :color="
                isTiltDisabled ||
                selectedTiltStrategy?.value !== TILT_STRATEGIES.DUAL_PRODUCT.BOTH_SAME
                  ? 'disabled'
                  : 'grey-lighten-2'
              "
              @click="
                !isTiltDisabled &&
                handleProductTilt(TILT_STRATEGY_OBJECTS.DUAL_PRODUCT.BOTH_SAME, false)
              "
            ></v-btn>
            <v-btn
              class="icon-btn"
              :class="
                selectedTiltStrategy?.value !== TILT_STRATEGIES.DUAL_PRODUCT.BOTH_SAME
                  ? 'text-medium-emphasis'
                  : ''
              "
              :disabled="isTiltDisabled"
              @click="
                !isTiltDisabled && changeTiltStrategy(TILT_STRATEGY_OBJECTS.DUAL_PRODUCT.BOTH_SAME)
              "
            >
              <svg width="20" height="16" viewBox="0 0 20 16" fill="none">
                <rect x="3" y="0" width="3" height="16" fill="currentColor" />
                <rect x="14" y="0" width="3" height="16" fill="currentColor" />
              </svg>
            </v-btn>
            <v-btn
              icon="mdi-rotate-right"
              :disabled="
                isTiltDisabled ||
                selectedTiltStrategy?.value !== TILT_STRATEGIES.DUAL_PRODUCT.BOTH_SAME
              "
              :color="
                isTiltDisabled ||
                selectedTiltStrategy?.value !== TILT_STRATEGIES.DUAL_PRODUCT.BOTH_SAME
                  ? 'disabled'
                  : 'grey-lighten-2'
              "
              @click="
                !isTiltDisabled &&
                handleProductTilt(TILT_STRATEGY_OBJECTS.DUAL_PRODUCT.BOTH_SAME, true)
              "
            ></v-btn>
          </v-btn-group>
          <!-- 2nd button: Left product only tilts -->
          <v-btn-group
            v-if="!isGlowShadowApplied"
            color="grey-lighten-2"
            density="comfortable"
            rounded="pill"
            class="mr-4 mb-2 tilt-option"
            :class="
              selectedTiltStrategy?.value === TILT_STRATEGIES.DUAL_PRODUCT.LEFT_ONLY
                ? 'tilt-option--active'
                : ''
            "
          >
            <v-btn
              icon="mdi-rotate-left"
              :disabled="
                isTiltDisabled ||
                selectedTiltStrategy?.value !== TILT_STRATEGIES.DUAL_PRODUCT.LEFT_ONLY
              "
              :color="
                isTiltDisabled ||
                selectedTiltStrategy?.value !== TILT_STRATEGIES.DUAL_PRODUCT.LEFT_ONLY
                  ? 'disabled'
                  : 'grey-lighten-2'
              "
              @click="
                !isTiltDisabled &&
                handleProductTilt(TILT_STRATEGY_OBJECTS.DUAL_PRODUCT.LEFT_ONLY, false)
              "
            ></v-btn>
            <v-btn
              class="icon-btn"
              :class="
                selectedTiltStrategy?.value !== TILT_STRATEGIES.DUAL_PRODUCT.LEFT_ONLY
                  ? 'text-medium-emphasis'
                  : ''
              "
              :disabled="isTiltDisabled"
              @click="
                !isTiltDisabled && changeTiltStrategy(TILT_STRATEGY_OBJECTS.DUAL_PRODUCT.LEFT_ONLY)
              "
            >
              <svg width="20" height="16" viewBox="0 0 20 16" fill="none">
                <rect
                  x="3"
                  y="0"
                  width="3"
                  height="16"
                  fill="currentColor"
                  transform="rotate(-25 8 4)"
                />
                <rect x="14" y="0" width="3" height="16" fill="currentColor" />
              </svg>
            </v-btn>
            <v-btn
              icon="mdi-rotate-right"
              :disabled="
                isTiltDisabled ||
                selectedTiltStrategy?.value !== TILT_STRATEGIES.DUAL_PRODUCT.LEFT_ONLY
              "
              :color="
                isTiltDisabled ||
                selectedTiltStrategy?.value !== TILT_STRATEGIES.DUAL_PRODUCT.LEFT_ONLY
                  ? 'disabled'
                  : 'grey-lighten-2'
              "
              @click="
                !isTiltDisabled &&
                handleProductTilt(TILT_STRATEGY_OBJECTS.DUAL_PRODUCT.LEFT_ONLY, true)
              "
            ></v-btn>
          </v-btn-group>
          <!-- 3rd button: Right product only tilts -->
          <v-btn-group
            v-if="!isGlowShadowApplied"
            color="grey-lighten-2"
            density="comfortable"
            rounded="pill"
            class="mr-4 mb-2 tilt-option"
            :class="
              selectedTiltStrategy?.value === TILT_STRATEGIES.DUAL_PRODUCT.RIGHT_ONLY
                ? 'tilt-option--active'
                : ''
            "
          >
            <v-btn
              icon="mdi-rotate-left"
              :disabled="
                isTiltDisabled ||
                selectedTiltStrategy?.value !== TILT_STRATEGIES.DUAL_PRODUCT.RIGHT_ONLY
              "
              :color="
                isTiltDisabled ||
                selectedTiltStrategy?.value !== TILT_STRATEGIES.DUAL_PRODUCT.RIGHT_ONLY
                  ? 'disabled'
                  : 'grey-lighten-2'
              "
              @click="
                !isTiltDisabled &&
                handleProductTilt(TILT_STRATEGY_OBJECTS.DUAL_PRODUCT.RIGHT_ONLY, false)
              "
            ></v-btn>
            <v-btn
              class="icon-btn"
              :class="
                selectedTiltStrategy?.value !== TILT_STRATEGIES.DUAL_PRODUCT.RIGHT_ONLY
                  ? 'text-medium-emphasis'
                  : ''
              "
              :disabled="isTiltDisabled"
              @click="
                !isTiltDisabled && changeTiltStrategy(TILT_STRATEGY_OBJECTS.DUAL_PRODUCT.RIGHT_ONLY)
              "
            >
              <svg width="20" height="16" viewBox="0 0 20 16" fill="none">
                <rect x="3" y="0" width="3" height="16" fill="currentColor" />
                <rect
                  x="14"
                  y="0"
                  width="3"
                  height="18"
                  fill="currentColor"
                  transform="rotate(25 12 4)"
                />
              </svg>
            </v-btn>
            <v-btn
              icon="mdi-rotate-right"
              :disabled="
                isTiltDisabled ||
                selectedTiltStrategy?.value !== TILT_STRATEGIES.DUAL_PRODUCT.RIGHT_ONLY
              "
              :color="
                isTiltDisabled ||
                selectedTiltStrategy?.value !== TILT_STRATEGIES.DUAL_PRODUCT.RIGHT_ONLY
                  ? 'disabled'
                  : 'grey-lighten-2'
              "
              @click="
                !isTiltDisabled &&
                handleProductTilt(TILT_STRATEGY_OBJECTS.DUAL_PRODUCT.RIGHT_ONLY, true)
              "
            ></v-btn>
          </v-btn-group>
          <!-- 4th button: Opposite tilts -->
          <v-btn-group
            v-if="!isGlowShadowApplied"
            color="grey-lighten-2"
            density="comfortable"
            rounded="pill"
            class="tilt-option mb-2"
            :class="
              selectedTiltStrategy?.value === TILT_STRATEGIES.DUAL_PRODUCT.OPPOSITE
                ? 'tilt-option--active'
                : ''
            "
          >
            <v-btn
              icon="mdi-rotate-left"
              :disabled="
                isTiltDisabled ||
                selectedTiltStrategy?.value !== TILT_STRATEGIES.DUAL_PRODUCT.OPPOSITE
              "
              :color="
                isTiltDisabled ||
                selectedTiltStrategy?.value !== TILT_STRATEGIES.DUAL_PRODUCT.OPPOSITE
                  ? 'disabled'
                  : 'grey-lighten-2'
              "
              @click="
                !isTiltDisabled &&
                handleProductTilt(TILT_STRATEGY_OBJECTS.DUAL_PRODUCT.OPPOSITE, false)
              "
            ></v-btn>
            <v-btn
              class="icon-btn"
              :class="
                selectedTiltStrategy?.value !== TILT_STRATEGIES.DUAL_PRODUCT.OPPOSITE
                  ? 'text-medium-emphasis'
                  : ''
              "
              :disabled="isTiltDisabled"
              @click="
                !isTiltDisabled && changeTiltStrategy(TILT_STRATEGY_OBJECTS.DUAL_PRODUCT.OPPOSITE)
              "
            >
              <svg width="20" height="16" viewBox="0 0 20 16" fill="none">
                <rect
                  x="3"
                  y="0"
                  width="3"
                  height="16"
                  fill="currentColor"
                  transform="rotate(-15 4.5 8)"
                />
                <rect
                  x="14"
                  y="0"
                  width="3"
                  height="16"
                  fill="currentColor"
                  transform="rotate(15 15.5 8)"
                />
              </svg>
            </v-btn>
            <v-btn
              icon="mdi-rotate-right"
              :disabled="
                isTiltDisabled ||
                selectedTiltStrategy?.value !== TILT_STRATEGIES.DUAL_PRODUCT.OPPOSITE
              "
              :color="
                isTiltDisabled ||
                selectedTiltStrategy?.value !== TILT_STRATEGIES.DUAL_PRODUCT.OPPOSITE
                  ? 'disabled'
                  : 'grey-lighten-2'
              "
              @click="
                !isTiltDisabled &&
                handleProductTilt(TILT_STRATEGY_OBJECTS.DUAL_PRODUCT.OPPOSITE, true)
              "
            ></v-btn>
          </v-btn-group>
        </template>

        <!-- 3 products: 3 controls -->
        <template v-else-if="productCount === 3">
          <!-- 1st button: All products tilt same direction -->
          <v-btn-group
            color="grey-lighten-2"
            density="comfortable"
            rounded="pill"
            class="mr-4 mb-2 tilt-option"
            :class="
              selectedTiltStrategy?.value === TILT_STRATEGIES.TRIPLE_PRODUCT.ALL_SAME
                ? 'tilt-option--active'
                : ''
            "
          >
            <v-btn
              icon="mdi-rotate-left"
              :disabled="
                isTiltDisabled ||
                selectedTiltStrategy?.value !== TILT_STRATEGIES.TRIPLE_PRODUCT.ALL_SAME
              "
              :color="
                isTiltDisabled ||
                selectedTiltStrategy?.value !== TILT_STRATEGIES.TRIPLE_PRODUCT.ALL_SAME
                  ? 'disabled'
                  : 'grey-lighten-2'
              "
              @click="
                !isTiltDisabled &&
                handleProductTilt(TILT_STRATEGY_OBJECTS.TRIPLE_PRODUCT.ALL_SAME, false)
              "
            ></v-btn>
            <v-btn
              class="icon-btn"
              :class="
                selectedTiltStrategy?.value !== TILT_STRATEGIES.TRIPLE_PRODUCT.ALL_SAME
                  ? 'text-medium-emphasis'
                  : ''
              "
              :disabled="isTiltDisabled"
              @click="
                !isTiltDisabled && changeTiltStrategy(TILT_STRATEGY_OBJECTS.TRIPLE_PRODUCT.ALL_SAME)
              "
            >
              <svg width="20" height="16" viewBox="0 0 20 16" fill="none">
                <rect x="3" y="0" width="3" height="16" fill="currentColor" />
                <rect x="8.5" y="0" width="3" height="16" fill="currentColor" />
                <rect x="14" y="0" width="3" height="16" fill="currentColor" />
              </svg>
            </v-btn>
            <v-btn
              icon="mdi-rotate-right"
              :disabled="
                isTiltDisabled ||
                selectedTiltStrategy?.value !== TILT_STRATEGIES.TRIPLE_PRODUCT.ALL_SAME
              "
              :color="
                isTiltDisabled ||
                selectedTiltStrategy?.value !== TILT_STRATEGIES.TRIPLE_PRODUCT.ALL_SAME
                  ? 'disabled'
                  : 'grey-lighten-2'
              "
              @click="
                !isTiltDisabled &&
                handleProductTilt(TILT_STRATEGY_OBJECTS.TRIPLE_PRODUCT.ALL_SAME, true)
              "
            ></v-btn>
          </v-btn-group>
          <!-- 2nd button: Fan left (left more, middle less, right unchanged) -->
          <v-btn-group
            v-if="!isGlowShadowApplied"
            color="grey-lighten-2"
            density="comfortable"
            rounded="pill"
            class="mr-4 mb-2 tilt-option"
            :class="
              selectedTiltStrategy?.value === TILT_STRATEGIES.TRIPLE_PRODUCT.FAN_LEFT
                ? 'tilt-option--active'
                : ''
            "
          >
            <v-btn
              icon="mdi-rotate-left"
              :disabled="
                isTiltDisabled ||
                selectedTiltStrategy?.value !== TILT_STRATEGIES.TRIPLE_PRODUCT.FAN_LEFT
              "
              :color="
                isTiltDisabled ||
                selectedTiltStrategy?.value !== TILT_STRATEGIES.TRIPLE_PRODUCT.FAN_LEFT
                  ? 'disabled'
                  : 'grey-lighten-2'
              "
              @click="
                !isTiltDisabled &&
                handleProductTilt(TILT_STRATEGY_OBJECTS.TRIPLE_PRODUCT.FAN_LEFT, false)
              "
            ></v-btn>
            <v-btn
              class="icon-btn"
              :class="
                selectedTiltStrategy?.value !== TILT_STRATEGIES.TRIPLE_PRODUCT.FAN_LEFT
                  ? 'text-medium-emphasis'
                  : ''
              "
              :disabled="isTiltDisabled"
              @click="
                !isTiltDisabled && changeTiltStrategy(TILT_STRATEGY_OBJECTS.TRIPLE_PRODUCT.FAN_LEFT)
              "
            >
              <svg width="30" height="16" viewBox="0 0 20 16" fill="none">
                <rect
                  x="8.5"
                  y="0"
                  width="3"
                  height="16"
                  fill="currentColor"
                  transform="rotate(-45 10 18)"
                />
                <rect
                  x="8.5"
                  y="0"
                  width="3"
                  height="16"
                  fill="currentColor"
                  transform="rotate(-23 10 17.5)"
                />
                <rect
                  x="8.5"
                  y="0"
                  width="3"
                  height="16"
                  fill="currentColor"
                  transform="rotate(0 10 16)"
                />
              </svg>
            </v-btn>
            <v-btn
              icon="mdi-rotate-right"
              :disabled="
                isTiltDisabled ||
                selectedTiltStrategy?.value !== TILT_STRATEGIES.TRIPLE_PRODUCT.FAN_LEFT
              "
              :color="
                isTiltDisabled ||
                selectedTiltStrategy?.value !== TILT_STRATEGIES.TRIPLE_PRODUCT.FAN_LEFT
                  ? 'disabled'
                  : 'grey-lighten-2'
              "
              @click="
                !isTiltDisabled &&
                handleProductTilt(TILT_STRATEGY_OBJECTS.TRIPLE_PRODUCT.FAN_LEFT, true)
              "
            ></v-btn>
          </v-btn-group>
          <!-- 3rd button: Side tilt (left and right tilt, middle unchanged) -->
          <v-btn-group
            v-if="!isGlowShadowApplied"
            color="grey-lighten-2"
            density="comfortable"
            rounded="pill"
            class="tilt-option mb-2"
            :class="
              selectedTiltStrategy?.value === TILT_STRATEGIES.TRIPLE_PRODUCT.SIDE_TILT
                ? 'tilt-option--active'
                : ''
            "
          >
            <v-btn
              icon="mdi-rotate-left"
              :disabled="
                isTiltDisabled ||
                selectedTiltStrategy?.value !== TILT_STRATEGIES.TRIPLE_PRODUCT.SIDE_TILT
              "
              :color="
                isTiltDisabled ||
                selectedTiltStrategy?.value !== TILT_STRATEGIES.TRIPLE_PRODUCT.SIDE_TILT
                  ? 'disabled'
                  : 'grey-lighten-2'
              "
              @click="
                !isTiltDisabled &&
                handleProductTilt(TILT_STRATEGY_OBJECTS.TRIPLE_PRODUCT.SIDE_TILT, false)
              "
            ></v-btn>
            <v-btn
              class="icon-btn"
              :class="
                selectedTiltStrategy?.value !== TILT_STRATEGIES.TRIPLE_PRODUCT.SIDE_TILT
                  ? 'text-medium-emphasis'
                  : ''
              "
              :disabled="isTiltDisabled"
              @click="
                !isTiltDisabled &&
                changeTiltStrategy(TILT_STRATEGY_OBJECTS.TRIPLE_PRODUCT.SIDE_TILT)
              "
            >
              <svg width="20" height="16" viewBox="0 0 20 16" fill="none">
                <rect
                  x="3"
                  y="0"
                  width="3"
                  height="16"
                  fill="currentColor"
                  transform="rotate(-25 4.5 8)"
                />
                <rect x="8.5" y="0" width="3" height="16" fill="currentColor" />
                <rect
                  x="14"
                  y="0"
                  width="3"
                  height="16"
                  fill="currentColor"
                  transform="rotate(25 15.5 8)"
                />
              </svg>
            </v-btn>
            <v-btn
              icon="mdi-rotate-right"
              :disabled="
                isTiltDisabled ||
                selectedTiltStrategy?.value !== TILT_STRATEGIES.TRIPLE_PRODUCT.SIDE_TILT
              "
              :color="
                isTiltDisabled ||
                selectedTiltStrategy?.value !== TILT_STRATEGIES.TRIPLE_PRODUCT.SIDE_TILT
                  ? 'disabled'
                  : 'grey-lighten-2'
              "
              @click="
                !isTiltDisabled &&
                handleProductTilt(TILT_STRATEGY_OBJECTS.TRIPLE_PRODUCT.SIDE_TILT, true)
              "
            ></v-btn>
          </v-btn-group>
        </template>
      </v-col>
    </v-row>
    <v-row class="align-center mt-0">
      <v-col class="text-start pt-1 text-subtitle-1 text-medium-emphasis d-flex" cols="3">
        <div>
          {{ t('labels.shadow') }}
        </div>
        <v-btn
          v-if="isGlowShadowSupported"
          density="compact"
          variant="flat"
          color="grey-lighten-2"
          icon
          class="ml-2"
          @click="isGlowShadowApplied ? applyGlowShadow(true) : applyGlowShadow(false)"
        >
          <v-icon
            size="small"
            :icon="isGlowShadowApplied ? 'mdi-lightbulb-on' : 'mdi-lightbulb-on-outline'"
            :color="isGlowShadowApplied ? 'primary' : ''"
          ></v-icon>
        </v-btn>
      </v-col>
      <v-col class="text-start">
        <v-btn-group
          color="grey-lighten-2"
          density="comfortable"
          rounded="pill"
          v-if="!isGlowShadowApplied"
        >
          <v-btn
            icon="mdi-minus"
            :disabled="shadowOpacity <= 0.01 || isGlowShadowApplied"
            :color="shadowOpacity <= 0.01 || isGlowShadowApplied ? 'disabled' : 'grey-lighten-2'"
            @click="updateShadowBrightness(false)"
          ></v-btn>
          <v-btn icon="mdi-brightness-6" readonly></v-btn>
          <v-btn
            icon="mdi-plus"
            :disabled="shadowOpacity >= 1 || isGlowShadowApplied"
            :color="shadowOpacity >= 1 || isGlowShadowApplied ? 'disabled' : 'grey-lighten-2'"
            @click="updateShadowBrightness(true)"
          ></v-btn>
        </v-btn-group>
        <v-btn-group
          v-if="isDropShadow && !isAIShadow"
          color="grey-lighten-2"
          density="comfortable"
          rounded="pill"
          class="ml-4"
        >
          <v-btn icon="mdi-minus" @click="expandDropShadow(false)"></v-btn>
          <v-btn icon="mdi-arrow-expand" readonly></v-btn>
          <v-btn icon="mdi-plus" @click="expandDropShadow(true)"></v-btn>
        </v-btn-group>
        <v-menu :close-on-content-click="false" v-if="isGlowShadowSupported && isGlowShadowApplied">
          <template #activator="{ props }">
            <v-btn
              v-bind="props"
              variant="flat"
              size="x-small"
              class="ml-2 border glow-color-picker"
              :class="{ 'mt-2 mb-3': isGlowShadowApplied }"
              :color="glowColor || undefined"
            ></v-btn>
          </template>
          <v-color-picker :model-value="glowColor" @update:model-value="handleGlowColorChange" :modes="['hexa']"></v-color-picker>
        </v-menu>
      </v-col>
    </v-row>
    <v-row class="align-center mt-2" v-if="supportedShadowDirections.length">
      <v-col class="text-start pt-1 text-subtitle-1 text-medium-emphasis" cols="3">
        {{ t('labels.lighting') }}
      </v-col>
      <v-col class="text-start">
        <v-btn-group color="grey-lighten-2" density="comfortable" rounded="pill">
          <v-btn icon="mdi-chevron-left" @click="updateShadowLighting(true)"></v-btn>
          <v-btn icon="mdi-car-light-dimmed" readonly></v-btn>
          <v-btn icon="mdi-chevron-right" @click="updateShadowLighting(false)"></v-btn>
        </v-btn-group>
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup lang="ts">
import { computed, onMounted, watch } from 'vue'
import { Shadow } from 'fabric'
import { useI18n } from 'vue-i18n'

import { useCanvasStore } from '@/stores/canvas'
import { useEditorStore } from '@/stores/editor'
import { usePlacementStore } from '@/stores/placement'
import { useProductStore } from '@/stores/product'
import { TILT_STRATEGIES } from '@/constants/advanceSettings'
import type { TiltStrategy } from '@/stores/editor'
import {
  DIRECTIONS,
  PLACEMENT_TYPES,
  PRODUCT_ASPECT_TYPES,
  SHADOW_TYPES,
  SPECIAL_PLACEMENTS,
} from '@/constants/positions'
import { AMBIENT_SHADOW_COLOR, DROP_SHADOW_COLOR } from '@/helpers/shadowHelper'
import UndoRedoService from '@/services/undoRedoService'
import EditorService from '@/services/editorService'
import { useAdSizeConfigStore } from '@/stores/adSizeConfig'
import { useCreativeStore } from '@/stores/creative'
import type { ProductWithInfo } from '@/types/product'
import { layerNames } from '@/constants/layers'

const {} = defineProps(['disableProductPlacement', 'isShufflingPlacement'])

const { t } = useI18n()
const placementStore = usePlacementStore()
const canvasStore = useCanvasStore()
const editorStore = useEditorStore()
const productStore = useProductStore()
const creativeStore = useCreativeStore()
const adSizeConfigStore = useAdSizeConfigStore()
const undoRedoService = new UndoRedoService()
const editorService = new EditorService()

// Replace local refs with computed properties from editorStore
const shadowOpacity = computed(() => editorStore.shadowOpacity)
const isDropShadow = computed(() => editorStore.isDropShadow)
const isAIShadow = computed(() => editorStore.isAiShadow)
const glowColor = computed({
  get: () => editorStore.glowColor,
  set: (value) => editorStore.setGlowColor(value),
})
const isGlowShadowApplied = computed(() => editorStore.isGlowShadowApplied)
const selectedTiltStrategy = computed(() => editorStore.selectedTiltStrategy)
const increaseProductSize = computed(() => editorStore.increaseProductSize)
const decreaseProductSize = computed(() => editorStore.decreaseProductSize)
const productCount = computed(() => editorStore.productCount)

const MIN_LEFT = computed(() => Math.min(placementStore.currentPlacementArea.left, adSizeConfigStore.minLeftConstraint))

// Helper to create TiltStrategy objects from constants
const createTiltStrategy = (name: string, value: string): TiltStrategy => ({ name, value })

// Create TiltStrategy objects for template use
const TILT_STRATEGY_OBJECTS = {
  SINGLE_PRODUCT: {
    LEFT: createTiltStrategy('Single Left', TILT_STRATEGIES.SINGLE_PRODUCT.LEFT),
  },
  DUAL_PRODUCT: {
    BOTH_SAME: createTiltStrategy('Both Same', TILT_STRATEGIES.DUAL_PRODUCT.BOTH_SAME),
    LEFT_ONLY: createTiltStrategy('Left Only', TILT_STRATEGIES.DUAL_PRODUCT.LEFT_ONLY),
    RIGHT_ONLY: createTiltStrategy('Right Only', TILT_STRATEGIES.DUAL_PRODUCT.RIGHT_ONLY),
    OPPOSITE: createTiltStrategy('Opposite', TILT_STRATEGIES.DUAL_PRODUCT.OPPOSITE),
  },
  TRIPLE_PRODUCT: {
    ALL_SAME: createTiltStrategy('All Same', TILT_STRATEGIES.TRIPLE_PRODUCT.ALL_SAME),
    FAN_LEFT: createTiltStrategy('Fan Left', TILT_STRATEGIES.TRIPLE_PRODUCT.FAN_LEFT),
    SIDE_TILT: createTiltStrategy('Side Tilt', TILT_STRATEGIES.TRIPLE_PRODUCT.SIDE_TILT),
  },
}

// Filtered Placement Options
const filteredPlacementOptions = computed(() => {
  if (placementStore.selectedBackgroundImage) {
    return placementStore.placementOptions
  }

  const products = editorStore.activeProductDataItems
  const allHorizontal =
    products.length >= 2 &&
    products.every((p) => p.productData?.aspectType === PRODUCT_ASPECT_TYPES.HORIZONTAL)

  return placementStore.placementOptions.filter(
    (option) =>
      SPECIAL_PLACEMENTS.includes(option) || (allHorizontal && option === PLACEMENT_TYPES.CORNER),
  )
})

// Set default tilt strategy based on
// product count
const defaultTiltStrategy = computed((): TiltStrategy | null => {
  switch (productCount.value) {
    case 1:
      return TILT_STRATEGY_OBJECTS.SINGLE_PRODUCT.LEFT
    case 2:
      if (placementStore.selectedPlacementOption == PLACEMENT_TYPES.FRONT) {
        return TILT_STRATEGY_OBJECTS.DUAL_PRODUCT.OPPOSITE
      }
      return TILT_STRATEGY_OBJECTS.DUAL_PRODUCT.BOTH_SAME
    case 3:
      if (placementStore.selectedPlacementOption == PLACEMENT_TYPES.FRONT) {
        return TILT_STRATEGY_OBJECTS.TRIPLE_PRODUCT.SIDE_TILT
      }
      return TILT_STRATEGY_OBJECTS.TRIPLE_PRODUCT.ALL_SAME
    default:
      return null
  }
})

const supportedShadowDirections = computed((): string[] => {
  if (isGlowShadowApplied.value) {
    return []
  }

  if (
    placementStore.selectedPlacementOption == PLACEMENT_TYPES.TOP ||
    placementStore.selectedPlacementOption == PLACEMENT_TYPES.STACK ||
    placementStore.selectedPlacementOption == PLACEMENT_TYPES.CORNER
  ) {
    return [DIRECTIONS.TOP_LEFT, DIRECTIONS.TOP_RIGHT, DIRECTIONS.BOTTOM_LEFT]
  }

  return []
})

const isGlowShadowSupported = computed((): boolean => {
  if (
    placementStore.selectedPlacementOption == PLACEMENT_TYPES.FLOAT ||
    (placementStore.selectedPlacementOption == PLACEMENT_TYPES.CORNER &&
      !placementStore.aiBackgroundImages.includes(placementStore.selectedAIBackgroundImage))
  ) {
    return true
  }

  return false
})

const isTiltDisabled = computed((): boolean => {
  const productDataItems = editorStore.activeProducts

  if (
    placementStore.selectedPlacementOption === PLACEMENT_TYPES.FRONT &&
    productDataItems.some((p) => p.metaData && JSON.parse(p.metaData)['isMultiple'])
  ) {
    return true
  }

  return (
    placementStore.selectedPlacementOption === PLACEMENT_TYPES.STAIRCASE ||
    placementStore.selectedPlacementOption === PLACEMENT_TYPES.LAYER
  )
})

const updateShadowBrightness = (increase: boolean) => {
  undoRedoService.updateShadowBrightnessWithUndo(increase)
}

const handleProductTilt = async (strategy: TiltStrategy, increase: boolean) => {
  await undoRedoService.handleProductTiltWithUndo(strategy, increase)
}

const adjustRgbaOpacity = (rgba: string, value: number) => {
  const match = rgba.match(/rgba?\(\s*(\d+),\s*(\d+),\s*(\d+),\s*(\d*\.?\d+)\s*\)/)

  if (match) {
    const [, r, g, b, a] = match
    const newOpacity = Math.max(0.01, Math.min(1, parseFloat(a) + value))
    editorStore.setShadowOpacity(newOpacity)
    return `rgba(${r}, ${g}, ${b}, ${newOpacity})`
  }
}

const expandDropShadow = (increase: boolean) => {
  undoRedoService.expandDropShadowWithUndo(increase)
}

const updateShadowLighting = (left: boolean) => {
  undoRedoService.updateShadowLightingWithUndo(left)
}

const applyGlowShadow = (removeShadow: boolean) => {
  undoRedoService.applyGlowShadowWithUndo(removeShadow)
}

const handleGlowColorChange = (newValue: string | null) => {
  const oldValue = glowColor.value
  
  if (newValue && newValue !== AMBIENT_SHADOW_COLOR && newValue != DROP_SHADOW_COLOR && isGlowShadowSupported.value) {
    undoRedoService.applyGlowShadowWithUndo(false, newValue, oldValue ?? undefined)
  } else if (!isGlowShadowSupported.value) {
    // Call editorService directly - the background selection handles this in its combined undo/redo action
    editorService.applyGlowShadow(true)
  }
}

const detectShadowDirection = (shadow: Shadow): string => {
  if (shadow.offsetX === 0 && shadow.offsetY === 0 && shadow.color != 'rgba(0, 0, 0, 0.4)') {
    return SHADOW_TYPES.GLOW
  }

  if (shadow.offsetX === 0 && shadow.offsetY === 0 && shadow.color == 'rgba(0, 0, 0, 0.4)') {
    return SHADOW_TYPES.AMBIENT
  }

  const x = shadow.offsetX
  const y = shadow.offsetY

  if (x > 0 && y > 0) return DIRECTIONS.TOP_LEFT
  if (x < 0 && y > 0) return DIRECTIONS.TOP_RIGHT
  if (x > 0 && y < 0) return DIRECTIONS.BOTTOM_LEFT

  return DIRECTIONS.BEHIND
}

const scaleProducts = async (increase: boolean) => {
  await undoRedoService.scaleProductsWithUndo(increase)
}

const resetValues = () => {
  editorStore.setShadowLightingIndex(0)
  editorStore.setIsGlowShadowApplied(false)
  editorStore.setGlowColor(null)
  editorStore.setIncreaseProductSize(true)
  editorStore.setDecreaseProductSize(true)
  editorStore.setSelectedTiltStrategy(defaultTiltStrategy.value)
  editorStore.setGroupScaleX(1)
  editorStore.setGroupScaleY(1)
}

const setInitialValues = () => {
  if (canvasStore.fabricCanvas) {
    const glowGroup = canvasStore.fabricCanvas
      .getObjects()
      .find((obj: any) => obj.layerName == 'glowGroup' && obj.type == 'group')

    checkLeftProductOverflow()

    if (glowGroup && glowGroup.shadow) {
      editorStore.setIsGlowShadowApplied(true)
      editorStore.setGlowColor(glowGroup.shadow.color)
    }

    const shadow = canvasStore.fabricCanvas
      .getObjects()
      .find((obj: any) => obj.layerName?.includes('Shadow'))

    if (shadow && SPECIAL_PLACEMENTS.includes(placementStore.selectedPlacementOption)) {
      editorStore.setShadowOpacity(shadow.opacity)
      if (shadow.layerName == layerNames.AIShadow) {
        editorStore.setIsAiShadow(true)
      }
      editorStore.setIsDropShadow(true)
    } else {
      editorStore.setIsAiShadow(false)
      editorStore.setIsDropShadow(false)
      const product = canvasStore.fabricCanvas
        .getObjects()
        .find((obj: any) => obj.layerName?.includes('Product'))

      if (product && product.shadow) {
        adjustRgbaOpacity(product.shadow.color, 0)

        const currentDirection = detectShadowDirection(product.shadow)

        if (currentDirection == SHADOW_TYPES.GLOW) {
          editorStore.setIsGlowShadowApplied(true)
          editorStore.setGlowColor(product.shadow.color)
        } else {
          editorStore.setShadowLightingIndex(
            supportedShadowDirections.value.findIndex((direction) => direction == currentDirection),
          )
        }
      }
    }

    // Initialize original angles
    initOriginalAngles()
  }
}

// Initialize selected strategy when product count changes
watch(
  productCount,
  () => {
    editorStore.setSelectedTiltStrategy(defaultTiltStrategy.value)
  },
  { immediate: true },
)



watch(
  () => editorStore.isShufflingPlacement,
  (value) => {
    if (value == false) {
      resetValues()
      setInitialValues()
    }
  },
)

watch(
  () => canvasStore.canvasLoader,
  (value) => {
    if (value == false) {
      resetValues()
      setInitialValues()
    }
  },
)

// watch(
//   () => placementStore.selectedAIBackgroundImage,
//   (value) => {
//     if(value && placementStore.selectedPlacementOption == PLACEMENT_TYPES.CORNER){
//       editorStore.setIsGlowShadowApplied(false)
//       applyGlowShadow(false)
//       editorStore.setIsDropShadow(true)
//     }
//   }
// )

// Disable product scaling if glow group or leftmost product crosses the left boundary
const checkLeftProductOverflow = () => {
  const glowGroup = canvasStore.fabricCanvas
    .getObjects()
    .find((obj: any) => obj.layerName == 'glowGroup' && obj.type == 'group')

  if (glowGroup) {
    if (glowGroup.getBoundingRect().left <= MIN_LEFT.value) {
      editorStore.setIncreaseProductSize(false)
    }
  } else {
    const products = canvasStore.fabricCanvas
      .getObjects()
      .filter((obj: any) => obj.layerName.includes('Product'))
    const sortedProducts = [...products].sort((a, b) => {
      const aLeft = a.getBoundingRect().left
      const bLeft = b.getBoundingRect().left
      return aLeft - bLeft
    })
    if (sortedProducts[0].getBoundingRect().left <= MIN_LEFT.value) {
      editorStore.setIncreaseProductSize(false)
    }
  }
}

// Call this when changing tilt strategy
const changeTiltStrategy = (strategy: TiltStrategy) => {
  undoRedoService.changeTiltStrategyWithUndo(strategy)
}

// Keep initOriginalAngles for initialization (used in watch)
const initOriginalAngles = () => {
  if (!canvasStore.fabricCanvas) return

  const products = canvasStore.fabricCanvas
    .getObjects()
    .filter((obj: any) => obj.layerName?.includes('Product'))
  products.forEach((product: any) => {
    if (product.originalAngle === undefined) {
      product.originalAngle = product.angle || 0
    }
  })
}

onMounted(() => {
  resetValues()
  setInitialValues()
})
</script>

<style scoped>
.icon-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 30px;
  width: 34px;
  height: 40px;
}

.glow-color-picker {
  border-color: black !important;
}

.tilt-option {
  border: 1px solid transparent;
  transition: border-color 0.3s ease;
}

.tilt-option--active {
  border-color: rgb(var(--v-theme-primary)) !important;
}

.disabled-settings {
  pointer-events: none;
  opacity: 0.4;
}
</style>
